import type { MainGoal, OnlineCourse, ReferralSource, TrainingLevel, TrainingLocation } from '../../../types/registration';

export const PUBLIC_ONLINE_COURSE_OPTIONS: ReadonlyArray<{ value: OnlineCourse; label: string }> = [
  { value: 'core_training', label: 'Curso Treinamento do Core da Mari Mósca' },
  { value: 'legs_glutes', label: 'Curso Pernas e Glúteos Definidos' },
  { value: 'both', label: 'Ambos' },
  { value: 'not_yet', label: 'Não sou aluna' },
  { value: 'other', label: 'Outro' },
];

export const PUBLIC_TRAINING_LEVEL_OPTIONS: ReadonlyArray<{
  value: TrainingLevel | 'other';
  label: string;
  description?: string;
}> = [
  {
    value: 'beginner',
    label: 'Iniciante',
    description:
      'Tenho pouca ou nenhuma experiência com treinamento de força e costumo começar e interromper o treinamento. Tenho menos de 6 meses de prática com o treinamento de força.',
  },
  {
    value: 'intermediate',
    label: 'Intermediário',
    description: 'Treino de forma consistente, regular e direcionada há pelo menos 6 meses.',
  },
  {
    value: 'advanced',
    label: 'Avançado',
    description:
      'Tenho pelo menos 1 ano de experiência em treinamento de força com intensidade e esforço elevado realizado de forma sistemática. Já conquistei ganhos significativos de força e hipertrofia. Caso você treine de forma regular há mais de 1 ano, mas não teve ganhos/resultados significativos, marque a opção intermediário.',
  },
  {
    value: 'very_advanced',
    label: 'Muito Avançado',
    description:
      'Treino há muitos anos de forma consistente, tenho enorme experiência e consciência corporal e possuo resultados muito expressivos na mudança de composição corporal.',
  },
  { value: 'other', label: 'Outro' },
];

export const PUBLIC_TRAINING_LOCATION_OPTIONS: ReadonlyArray<{ value: TrainingLocation; label: string }> = [
  { value: 'full_gym', label: 'Academia Completa' },
  { value: 'home_condo_gym', label: 'Casa ou Academia de Prédio/Condomínio' },
];

export const PUBLIC_WEEKLY_AVAILABILITY_OPTIONS = Array.from({ length: 7 }, (_, index) => {
  const times = index + 1;
  const label = `${times}x na semana`;
  return { value: label, label };
}) as ReadonlyArray<{ value: string; label: string }>;

export const PUBLIC_AVAILABLE_TRAINING_TIME_OPTIONS = [
  '20 min',
  '30 min',
  '40 min',
  '50 min',
  '60 min',
  '90 min',
  '120 min',
].map((label) => ({ value: label, label })) as ReadonlyArray<{ value: string; label: string }>;

export const withCurrentSelectOption = (
  options: ReadonlyArray<{ value: string; label: string }>,
  currentValue: string,
): Array<{ value: string; label: string }> => {
  const trimmed = currentValue.trim();
  if (!trimmed || options.some((option) => option.value === trimmed)) {
    return [...options];
  }

  return [{ value: trimmed, label: trimmed }, ...options];
};

export const PUBLIC_MAIN_GOAL_OPTIONS: ReadonlyArray<{ value: MainGoal; label: string }> = [
  { value: 'hypertrophy', label: 'Hipertrofia' },
  { value: 'weight_loss', label: 'Emagrecimento' },
  { value: 'health_maintenance', label: 'Manutenção da saúde e melhora do condicionamento físico' },
  { value: 'muscle_definition', label: 'Definição muscular' },
];

export const PUBLIC_REFERRAL_SOURCE_OPTIONS: ReadonlyArray<{ value: ReferralSource; label: string }> = [
  { value: 'referral', label: 'Indicação' },
  { value: 'social_media', label: 'Instagram' },
  { value: 'other', label: 'Outros' },
];

export type PublicAnamneseObservationBlock = {
  paragraphs?: readonly string[];
  listItems?: readonly string[];
  attention?: string;
};

export type PublicAnamneseObservation = {
  bordered?: boolean;
  blocks: readonly PublicAnamneseObservationBlock[];
};

export const PUBLIC_ANAMNESE_OBSERVATIONS = {
  trainingLevel: {
    bordered: true,
    blocks: [
      {
        paragraphs: [
          'A classificação baseada no tempo de experiência é um ponto de partida para que eu possa elaborar o seu treinamento, mas o detalhamento das informações do seu histórico de saúde e prática de exercícios físicos e as suas fotos são FUNDAMENTAIS para que eu possa prescrever o melhor treino possível para a sua condição física atual e de acordo com os objetivos que você deseja atingir.',
        ],
      },
    ],
  },
  trainingLocation: {
    bordered: true,
    blocks: [
      {
        paragraphs: [
          'Caso deseje mudar o seu local de treino não há nenhum problema.',
          'Por gentileza, avisar por e-mail antes da data de renovação do seu plano para que eu possa fazer os ajustes necessários.',
          'Os treinos serão enviados para casa OU academia (academia completa ou de condomínio).',
          'Não é possível ter prescrições diferentes (exemplo: treino para fazer em casa e treino para fazer em academia).',
        ],
      },
    ],
  },
  homeTrainingMaterials: {
    bordered: true,
    blocks: [
      {
        paragraphs: [
          'Caso sua escolha seja treinar em casa, há a necessidade de um material mínimo.',
          'Estes materiais são:',
        ],
        listItems: [
          'Faixas elásticas do tipo Theraband de diferentes intensidades',
          'Mini Band',
          'Pesos livres (barra, anilhas e halteres)',
          'Bola suíça',
        ],
      },
      {
        paragraphs: [
          'Caso não tenha pesos, basta adaptar inicialmente de acordo com o guia de sobrecarga caseira.',
        ],
        attention:
          'Não serão enviados treinos para casa sem estes materiais, pois esse é o mínimo necessário para que você possa evoluir no programa de treinamento.',
      },
    ],
  },
  weeklyAvailability: {
    bordered: true,
    blocks: [
      {
        paragraphs: [
          'Não necessariamente o treino será prescrito de acordo com todo o seu tempo disponível.',
          'A prescrição será ajustada de acordo com a sua disponibilidade e objetivos.',
        ],
      },
    ],
  },
  movementLimitations: {
    bordered: true,
    blocks: [
      {
        paragraphs: [
          'Grande parte das limitações de movimento são geradas por desequilíbrios, fraquezas musculares e erros de execução dos movimentos.',
          'Meu trabalho envolve diminuir as limitações e, possivelmente, reinserir aos poucos os exercícios que geravam dor para que você possa ter um corpo forte e saudável.',
          'No Instagram posto conteúdo regular para auxiliar na execução dos movimentos e dentro do APP de treinamento há aulas inseridas para melhorar a mobilidade, flexibilidade e estabilidade do seu corpo, além de muitos vídeos ensinando a executar os exercícios do seu treinamento.',
        ],
      },
    ],
  },
  currentTraining: {
    bordered: true,
    blocks: [
      {
        paragraphs: [
          'Caso tenha sua última série prescrita ou mais detalhes sobre o seu treinamento atual, não deixe de descrever.',
          'Você também pode enviar junto com as suas fotos a série que está executando no momento.',
        ],
      },
    ],
  },
  nutritionalSupport: {
    bordered: true,
    blocks: [
      {
        paragraphs: [
          'O acompanhamento nutricional é fundamental para o processo de evolução da composição corporal, seja para emagrecimento ou ganho de massa magra.',
          'Caso não tenha um profissional no momento, lhe encorajo a iniciar esse cuidado.',
          'Sem dúvida será um complemento fundamental para obter os resultados que deseja.',
        ],
      },
    ],
  },
  postpartumDiastasis: {
    bordered: true,
    blocks: [
      {
        paragraphs: [
          'Parte do conteúdo do Curso de Treinamento do Core é focado em ensinar e orientar como deve acontecer o fortalecimento dos músculos abdominais após o parto e nos casos de diástase.',
          'Para esses casos o curso é muito recomendado e apesar de estar com as vagas encerradas, serão abertas exceções para alunas matriculadas na consultoria.',
        ],
      },
    ],
  },
} as const satisfies Record<string, PublicAnamneseObservation>;

export type PublicAnamneseObservationKey = keyof typeof PUBLIC_ANAMNESE_OBSERVATIONS;

export const PUBLIC_ANAMNESE_FIELD_LABELS = {
  email: 'E-mail',
  studentName: 'Nome completo',
  birthDate: 'Data de nascimento',
  heightCm: 'Estatura (m)',
  weightKg: 'Peso Corporal (kg)',
  cpf: 'CPF',
  phone: 'Seu número de WhatsApp (com o DDI do seu país e DDD do seu estado)',
  profession: 'Profissão',
  instagram: 'Instagram',
  previousOnlineConsultancy:
    'Já fez alguma Consultoria Online antes de me conhecer? Se sim, por que optou por interromper o outro serviço?',
  onlineCourse: 'Você é aluna de algum dos meus cursos online?',
  courseFeedback:
    'Caso seja aluna de algum dos meus cursos, me envie um feedback de como foi a experiência e em que fase do curso você está!',
  trainingLevel: 'Qual o seu nível de treinamento?',
  trainingLocation: 'Qual será o seu local de treino?',
  fullGymName:
    'Qual o nome da academia que você treina? É uma academia bem completa ou a opção de materiais é limitada?',
  availableEquipment:
    'Somente para quem vai treinar em casa ou academia do prédio: descreva todos os materiais de treinamento que possui e se tiver fotos dos equipamentos nos envie por e-mail',
  weeklyAvailability:
    'Qual a sua disponibilidade semanal total para os treinos da Consultoria? Seja realista com a sua rotina atual. Você pode alterar essa disponibilidade sempre que desejar.',
  availableTrainingTime: 'Qual o tempo disponível para cada treino?',
  hadInjury:
    'Você já sofreu algum tipo de acidente ou alguma lesão osteomuscular (exemplo: fratura óssea, estiramento muscular, hérnia de disco, ruptura de ligamento...)?',
  injuryDescription: 'Se SIM, conte-me sobre a lesão',
  hasPain: 'Ultimamente tem sentido dores em alguma região do corpo?',
  painLocation: 'Se SIM, em que local?',
  painLimitsTraining: 'Essa dor te causa algum tipo de LIMITAÇÃO no treinamento neste momento?',
  limitedExercises: 'Se SIM, que exercícios você não consegue realizar?',
  currentlyExercises: 'Você pratica exercícios físicos atualmente?',
  exerciseModalities: 'Se SIM, qual/quais?',
  timeWithoutExercise: 'Se NÃO, há quanto tempo está sem praticar exercícios?',
  exerciseHistory: 'Qual o seu histórico de prática de exercícios físicos (se houver)?',
  enjoysTraining: 'Você gosta e tem prazer em praticar exercícios?',
  usedToEffort:
    'Está acostumada a treinar sentindo desconforto durante o treinamento? (ou seja, você treina sentindo que de fato está levando o seu corpo a um esforço bastante alto?)',
  hasNutritionalSupport:
    'Você faz acompanhamento nutricional ou multidisciplinar para a saúde (Médico, Nutricionista, Psicólogo...)?',
  dietDetail:
    'Me conte um pouco mais sobre como é a sua alimentação de forma geral, como anda a qualidade do seu sono e a sua saúde mental.',
  mainGoal: 'Qual é o seu principal objetivo com a Consultoria de Treinamento?',
  goalDetails:
    'Me conte um pouco mais sobre os seus objetivos. Aproveite esse espaço para relatar tudo o que for conveniente relacionado aos seus desejos com o treinamento.',
  takesMedication: 'Faz uso de algum medicamento contínuo?',
  medications: 'Se marcou SIM acima, qual o medicamento, ou tipo de medicamento?',
  hadSurgery: 'Já realizou algum tipo de cirurgia?',
  surgeries: 'Se marcou SIM acima, qual?',
  hasChronicDisease: 'Tem alguma doença crônica?',
  chronicDiseases: 'Se marcou SIM acima, qual?',
  hasFamilyHistory: 'Tem histórico familiar de doenças como diabetes, hipertensão ou cardiopatia?',
  familyHistoryDescription: 'Se marcou SIM, me conte um pouquinho mais sobre o seu histórico familiar.',
  hasChildren: 'Tem filhos?',
  timeSinceDelivery:
    'Se SIM, há quanto tempo foi seu parto? (pós parto inicial - até 6 meses pós parto e tardio - 6 meses ou mais)',
  abdominalDiastasis: 'Se tem filhos, sabe se ficou com diástase após o parto? Tratou?',
  additionalInfo: 'Tem alguma informação que gostaria de acrescentar?',
  referralSource: 'Como chegou até mim?',
  motivation: 'Vou adorar saber como chegou até mim e o que te fez escolher iniciar a Consultoria comigo!',
} as const;
